import { inject, Injectable } from '@angular/core';
import { NotificationService } from '@services/notification.service';
import { NewsService } from '@services/stella-state-services/news.service';
import { CustomerService } from '@services/stella-state-services/customer.service';
import { PermissionService } from '@services/stella-state-services/permission.service';
import { UserService } from '@services/stella-state-services/user.service';
import { DocumentDeliveryService } from '@services/stella-state-services/document-delivery.service';
import { ChatUnreadService } from '@src/app/features/chat/services/chat-unread.service';
import { ContractsService } from '@services/stella-state-services/contracts.service';
import { ChatService } from '@src/app/features/chat/services/chat.service';

/**
 * Constructs the state services that feed the home/tabs screens at app start instead of
 * on first component injection.
 *
 * Their load pipelines are keyed off `authService.personId$`, so simply existing early means
 * their requests fire the moment the auth login resolves — while the loading screen is
 * still up — instead of only after routing and lazy-chunk loading complete. This removes
 * a full sequential phase from startup.
 */
@Injectable({
  providedIn: 'root'
})
export class StartupWarmupService {
  private readonly notificationService = inject(NotificationService);
  private readonly newsService = inject(NewsService);
  private readonly customerService = inject(CustomerService);
  private readonly permissionService = inject(PermissionService);
  private readonly userService = inject(UserService);
  private readonly chatUnreadService = inject(ChatUnreadService);
  private readonly documentDeliveryService = inject(DocumentDeliveryService);
  private readonly contractsService = inject(ContractsService);
  private readonly chatService = inject(ChatService);

  /**
   * Reloads the per-person data once. Keyed on `personId$`, the streams do not reload on their own
   * when the same person is still signed in after a backend outage, so whatever failed while the
   * app sat on the network-error screen would stay empty until a pull-to-refresh.
   */
  public reloadPersonData(): void {
    this.notificationService.reloadNotifications();
    this.newsService.reloadHomeNewsList();
    this.customerService.reloadCustomerList();
    this.permissionService.reloadPersonPermissions();
    this.userService.reloadBrokerUser();
    this.documentDeliveryService.reloadDeliveries();
    this.contractsService.reloadContractList();
    this.chatService.loadCustomerOverviews().subscribe();
  }
}
